import * as React from 'react';
import * as _ from 'underscore';

import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { ISettingsCoordinatorCtrlScope } from '../../../settings/directives/tbSettingsCoordinator';
import { ISiteBridgeSettingsForSiteAdmin } from '../../../settings/interfaces/ISiteSettingsForSiteAdmin';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { Sites } from '../../../ts/resources/Sites';
import { ServerService } from '../../../ts/services/ServerService';
import { ServiceLocator } from '../../ServiceLocator';
import { RouterError } from '../RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../SpacesSiteUrls';

const template = `<div ng-include="'siteSettingsShouldShowBridge.html'"></div>`;

export interface BridgeSiteSettingsSubspaceOwnProps {
  readonly urls: SpacesSiteUrls;
  readonly angularContext: AngularContext;
  readonly server: ServerService;
  readonly isSessionMultiSite: boolean;
  readonly isSessionServerAdmin: boolean;
}

export interface BridgeSiteSettingsSubspaceAngularProps {
  readonly sites: Sites;
}

export type BridgeSiteSettingsSubspaceProps = BridgeSiteSettingsSubspaceOwnProps & BridgeSiteSettingsSubspaceAngularProps;

export interface BridgeSiteSettingsSubspaceState {
  readonly hasDataToRender: boolean;
  readonly hasError: boolean;
  readonly settings: ISiteBridgeSettingsForSiteAdmin;
}

export class BridgeSiteSettingsSubspaceComponent extends React.PureComponent<BridgeSiteSettingsSubspaceProps, BridgeSiteSettingsSubspaceState> {

  private childScope: ISettingsCoordinatorCtrlScope = undefined;
  private unmounted: boolean = false;

  public constructor(props: BridgeSiteSettingsSubspaceProps) {
    super(props);
    this.state = {hasDataToRender: false, settings: undefined, hasError: false};
  }

  public componentDidMount() {
    this.getData();
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidUpdate() {
    if (!_.isUndefined(this.state.settings)) {
        this.updateState({hasDataToRender: true});
    }

    if (this.state.hasDataToRender && !_.isUndefined(this.childScope)) {
      _.defer(() => this.childScope.$apply());
    }
  }

  public render() {
    if (!this.state.hasDataToRender) {
      return null;
    } else if (this.state.hasError) {
      return <RouterError errorType={'default'} urls={this.props.urls} />;
    }

    // the parent div here is important to ensure AngularDirective plays nice when unmounting
    return (
      <div className={'tb-stylish-settings'}>
        <AngularDirective
          template={template}
          angularContext={this.props.angularContext}
          setupScope={this.setupScope}
        />
      </div>
    );
  }

  private updateState<K extends keyof BridgeSiteSettingsSubspaceState>(newState: Pick<BridgeSiteSettingsSubspaceState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private getData = async () => {
    try {
      await this.props.server.getSessionInfoPromise();
      const settings = await this.props.sites.getCurrentSiteBridgeSettingsForSiteAdmin();
      this.updateState({settings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private setupScope = (scope: ng.IScope) => {
    const childScope = scope.$new() as ISettingsCoordinatorCtrlScope;
    childScope.showEncryptionStatus = true;
    childScope.server = this.props.server;
    childScope.hideNameAndId = !this.props.isSessionMultiSite;
    childScope.siteSettings = this.state.settings;
    this.childScope = childScope;
    return childScope;
  }
}

const BridgeSiteSettingsHOC = angularProviderHOC<BridgeSiteSettingsSubspaceOwnProps, BridgeSiteSettingsSubspaceAngularProps, BridgeSiteSettingsSubspaceAngularProps>({
  getAngularServicesWithServiceLocator: () => ({
    sites: ServiceLocator.get<Sites>('Sites'),
  }),
  selectPropsFromAngularServices: angularServices => angularServices,
});

export const BridgeSiteSettingsSubspace: React.ComponentType<BridgeSiteSettingsSubspaceOwnProps>
  = BridgeSiteSettingsHOC(BridgeSiteSettingsSubspaceComponent);
